import React, { useState, useMemo, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Box, CircularProgress, Typography, ButtonGroup } from "@mui/material";
import PrimarySelect from "../../Inputs/PrimarySelect/PrimarySelect";
import PrimaryTextField from "../../Inputs/PrimaryTextField/PrimaryTextField";
import SecondaryButton from "../../Buttons/SecondaryButton/SecondaryButton";
import CustomizedTable from "../../CustomizedTable/CustomizedTable";
import SelectColumnFilter from "../../CustomizedTable/Filters/SelectColumnFilter/SelectColumnFilter";
import BrokerageStatusFilter from "../../CustomizedTable/Filters/BrokerageStatusFilter/BrokerageStatusFilter";
import MultiOptionsFilter from "../../CustomizedTable/Filters/MultipleOptionsFilter/MultipleOptionsFilter";
import NumberRangeFilter from "../../CustomizedTable/Filters/NumberRangeFilter/NumberRangeFilter";
import DateRangeFilter from "../../CustomizedTable/Filters/DateRangeFilter/DateRangeFilter";
import { DefaultCell, TitleCaseCell, UpCaseCell } from "../../CustomizedTable/CustomCells/CustomCells";
import BrokerageStatusCell from "../../CustomizedTable/CustomCells/BrokerageStatusCell/BrokerageStatusCell";
import PipelineStatusCell from "../../CustomizedTable/CustomCells/PipelineStatusCell/PipelineStatusCell";
import LoadIdCell from "../../CustomizedTable/CustomCells/OrderIdCell/LoadIdCell";
import DateTimeRangeCell from "../../CustomizedTable/CustomCells/DateTimeRangeCell/DateTimeRangeCell";
import CurrencyCell from "../../CustomizedTable/CustomCells/CurrencyCell/CurrencyCell";
import CarrierNameCell from "../../CustomizedTable/CustomCells/CarrierNameCell/CarrierNameCell";
import WeightCell from "../../CustomizedTable/CustomCells/WeightCell/WeightCell";
import ModeWithTempCell from "../../CustomizedTable/CustomCells/ModeWithTempCell/ModeWithTempCell";
import LoadProfileLaneHistoryScorecard from "./LoadProfileLaneHistoryScorecard/LoadProfileLaneHistoryScorecard";
import { LoadService } from "../../../services/LoadService";
import { AlertContext } from "../../Dashboard/Dashboard";

export default function LoadProfileLaneHistory({
  activeLoad,
  handleTableAction
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isOfferBoard, setIsOfferBoard] = useState(false);
  const [title, setTitle] = useState("Lane History");
  const [type, setType] = useState("lane");
  const [laneHistoryLoads, setLaneHistoryLoads] = useState([]);
  const [scorecardData, setScorecardData] = useState([]);
  const [search, setSearch] = useState("");
  const [radiusOrigin, setRadiusOrigin] = useState(50);
  const [radiusDest, setRadiusDest] = useState(50);

  const radiusOptions = useMemo(() => [
    {
      name: "25 mi",
      value: "25"
    },
    {
      name: "50 mi",
      value: "50"
    },
    {
      name: "100 mi",
      value: "100"
    },
    {
      name: "150 mi",
      value: "150"
    },
    {
      name: "200 mi",
      value: "200"
    },
    {
      name: "250 mi",
      value: "250"
    },
    {
      name: "300 mi",
      value: "300"
    },
    {
      name: "350 mi",
      value: "350"
    },
    {
      name: "400 mi",
      value: "400"
    },
    {
      name: "450 mi",
      value: "450"
    },
    {
      name: "500 mi",
      value: "500"
    }
  ], []);

  const loads = useMemo(() => laneHistoryLoads, [laneHistoryLoads]);

  const columns = useMemo(
    () => [
      {
        Header: "Status",
        accessor: "brokerageStatus",
        Filter: BrokerageStatusFilter,
        filter: "multi",
        width: 100,
        unSelected: type === "lane" && ["AVA"],
        preSelected: type === "load" && ["COV", "DIS", "LOA", "LOAD", "DEL", "DELV"],
        Cell: BrokerageStatusCell,
      },
      {
        Header: "Load",
        accessor: "id",
        filter: "fuzzyText",
        width: 90,
        Cell: LoadIdCell,
      },
      {
        Header: "Mode",
        accessor: "mode",
        Filter: SelectColumnFilter,
        filter: "equal",
        width: 90,
        Cell: ModeWithTempCell,
      },
      {
        Header: "Origin City",
        accessor: "originCity",
        filter: "fuzzyText",
        width: 130,
        Cell: UpCaseCell,
      },
      {
        Header: "OS",
        accessor: "originState",
        Filter: MultiOptionsFilter,
        filter: "multi",
        width: 80,
        Cell: DefaultCell,
      },
      {
        Header: "Pick Up",
        accessor: "originSchedArriveEarly",
        Filter: DateRangeFilter,
        filter: "dateBetween",
        lateDate: "originSchedArriveLate",
        stopType: "Origin",
        width: 140,
        Cell: DateTimeRangeCell,
      },
      {
        Header: "Dest City",
        accessor: "destinationCity",
        filter: "fuzzyText",
        width: 140,
        Cell: UpCaseCell,
      },
      {
        Header: "DS",
        accessor: "destinationState",
        Filter: MultiOptionsFilter,
        filter: "multi",
        width: 80,
        Cell: DefaultCell,
      },
      {
        Header: "Delivery",
        accessor: "destinationSchedArriveEarly",
        Filter: DateRangeFilter,
        filter: "dateBetween",
        lateDate: "destinationSchedArriveLate",
        stopType: "Destination",
        width: 140,
        Cell: DateTimeRangeCell,
      },
      {
        Header: "Customer",
        accessor: "customerName",
        filter: "fuzzyText",
        width: 320,
        Cell: DefaultCell,
      },
      {
        Header: "Customer Charge",
        accessor: "orderTotalCharge",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 160,
        Cell: CurrencyCell,
      },
      {
        Header: "Carrier",
        accessor: "carrierName",
        filter: "fuzzyText",
        idKey: "carrierId",
        originPoint: "load-history",
        showCanBook: true,
        width: 240,
        Cell: CarrierNameCell,
      },
      {
        Header: "Pipeline Status",
        accessor: "pipelineStatus",
        Filter: SelectColumnFilter,
        filter: "equal",
        width: 160,
        Cell: PipelineStatusCell,
      },
      {
        Header: "Carrier Rep",
        accessor: "dispatcherUserId",
        filter: "fuzzyText",
        width: 140,
        Cell: DefaultCell,
      },
      {
        Header: "Carrier LH",
        accessor: "carrierLineHaul",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 140,
        Cell: CurrencyCell,
      },
      {
        Header: "Carrier FSC",
        accessor: "carrierFuelSurcharge",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 140,
        Cell: CurrencyCell,
      },
      {
        Header: "Carrier Pay",
        accessor: "carrierPay",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 140,
        Cell: CurrencyCell,
      },
      {
        Header: "Acct Mgr",
        accessor: "customerRep",
        filter: "fuzzyText",
        width: 110,
        Cell: DefaultCell,
      },
      {
        Header: "Commodity",
        accessor: "commodity",
        filter: "fuzzyText",
        width: 140,
        Cell: TitleCaseCell,
      },
      {
        Header: "Weight",
        accessor: "weight",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 110,
        Cell: WeightCell,
      }
    ],
    [type]
  );

  const handleHistoryChange = () => {
    setIsLoading(true);
    setTitle(type === "lane" ? "Lane History" : "Load History");
    const fetchHistory = type === "lane" ? LoadService.GetLaneHistory : LoadService.GetLoadHistory;
    const originStop = activeLoad.stops.length && activeLoad.stops[0];
    const destinationStop = activeLoad.stops.length && activeLoad.stops[activeLoad.stops.length - 1];
    const params = type === "lane"
      ? [instance, activeLoad.id, radiusOrigin, radiusDest, activeLoad.revenueCodeId]
      : [instance, originStop.cityName, originStop.state, destinationStop.cityName, destinationStop.state, activeLoad.revenueCodeId];

    fetchHistory(...params).then(res => {
      res.loads.forEach(load => {
        load.carrierPhoneNumber = load.phoneNumber;
      });
      setLaneHistoryLoads(type === "lane" ? res.loads.filter(({ brokerageStatus }) => brokerageStatus !== "AVA") : res.loads);

      const scorecardData = type === "lane" ? res.laneHistoryHeader : res.loadHistoryHeader;
      setScorecardData([
        {
          label: "Avg. Carrier All-In",
          value: `$${Math.round(scorecardData?.averageCarrierAllIn)}`
        },
        {
          label: "Avg. Customer All-In",
          value: `$${Math.round(scorecardData?.averageCustomerAllIn)}`
        },
        {
          label: "Avg. Miles",
          value: Math.round(scorecardData?.averageMiles)
        },
        {
          label: "Unique Carriers",
          value: scorecardData?.numberOfCarriers
        },
        {
          label: "Orders",
          value: scorecardData?.numberOfOrder
        },
      ]);
      setIsLoading(false);
    }).catch(error => {
      console.error(error);
      setIsLoading(false);
      warning(error);
    });

  };

  useEffect(() => {
    if (activeLoad.id) {
      handleHistoryChange();
    }
  }, [activeLoad, radiusOrigin, radiusDest, type]);

  useEffect(() => {
    const paths = window.location.pathname.split("/");
    setIsOfferBoard(paths.includes("offer-boards"));
  }, [window.location]);

  return (
    <Box className="Load-profile-component">
      <Box className="Load-profile-component-header">
        <Typography variant="h3" color="black_opacity.100">
          {title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ButtonGroup
            variant="outlined"
            sx={{ height: "28px", marginRight: "10px" }}
            disableElevation
          >
            <SecondaryButton
              label="Load"
              onClick={() => setType("load")}
              disabled={isLoading}
            />
            <SecondaryButton
              label="Lane"
              onClick={() => setType("lane")}
              disabled={isLoading}
            />
          </ButtonGroup>
          <Box sx={{ display: "flex", marginRight: "10px" }}>
            <Typography variant="body1" color="black_opacity.100" sx={{ marginRight: "8px" }}>
              Time Frame: 1 yr
            </Typography>
          </Box>
          {type === "lane" &&
            <>
              <Box sx={{ display: "flex", marginRight: "10px" }}>
                <Typography variant="body1" color="black_opacity.100" sx={{ marginRight: "8px" }}>
                  Origin:
                </Typography>
                <PrimarySelect
                  options={radiusOptions}
                  onChange={setRadiusOrigin}
                  value={radiusOrigin}
                  variant="standard"
                  nameKey="name"
                  valueKey="value"
                  width="70px"
                  disabled={isLoading}
                />
              </Box>
              <Box sx={{ display: "flex", marginRight: "10px" }}>
                <Typography variant="body1" color="black_opacity.100" sx={{ marginRight: "8px" }}>
                  Dest:
                </Typography>
                <PrimarySelect
                  options={radiusOptions}
                  onChange={setRadiusDest}
                  value={radiusDest}
                  variant="standard"
                  nameKey="name"
                  valueKey="value"
                  width="70px"
                  disabled={isLoading}
                />
              </Box>
            </>
          }
          <PrimaryTextField
            placeholder={`Search ${loads.length} results`}
            onChange={setSearch}
            value={search}
            isSearch
          />
        </Box>
      </Box>
      {!isLoading && !isOfferBoard && <LoadProfileLaneHistoryScorecard scorecardData={scorecardData} />}
      {isLoading ?
        (
          <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
            <CircularProgress sx={{ color: "primary.60" }} size={40} />
          </Box>
        ) : (
          <CustomizedTable
            id="load-profile-lane-history"
            columns={columns}
            data={loads}
            tableSearch={search}
            actionCallback={handleTableAction}
          />
        )
      }
    </Box>
  );
}

LoadProfileLaneHistory.propTypes = {
  activeLoad: PropTypes.object.isRequired,
  handleTableAction: PropTypes.func,
};